import { Flex, Headings, Table } from '@foradevelopment/fora-ui';
import { PayMonthOverviewViewModel, PayMonthStatusEnum } from '@fora-monorepo/employer-my-pages-common/types';
import { isReportingPeriodOpen } from '@fora-monorepo/employer-my-pages-common/utils';

import { PayReportsTableRow } from './PayReportsTableRow';
import { PayReportsTableSortIdKeys, PayReportsTableSortState } from './PayReportsListSortState';
import { useTypedTranslation } from '../../../i18n/i18n';

export const PayReportsTable = ({
  payMonthOverviews: reportingPeriods,
  sortedPayMonthIndices,
  sortTable,
  sortState,
}: {
  payMonthOverviews: PayMonthOverviewViewModel[];
  sortedPayMonthIndices: number[];
  sortTable: (sortId: PayReportsTableSortIdKeys) => void;
  sortState: PayReportsTableSortState;
}) => {
  const { t } = useTypedTranslation();

  return (
    <Table withExpandableRows={reportingPeriods.length > 0} withBorderedRows layoutFixed>
      <Table.Head>
        <Table.HeadRow>
          <Table.HeadCell
            key="pay-report-reportingPeriods-column"
            sortId="reportingPeriods"
            onToggle={sortTable as (id: string) => void}
            state={sortState['reportingPeriods']}
          >
            {t('table.reportOverview.column1')}
          </Table.HeadCell>
          <Table.HeadCell
            key="pay-report-status-column"
            sortId="status"
            onToggle={sortTable as (id: string) => void}
            state={sortState['status']}
            textAlign="right"
          >
            {t('table.invoice.columnInvoiceStatus')}
          </Table.HeadCell>
        </Table.HeadRow>
      </Table.Head>
      <Table.Body>
        {reportingPeriods.length > 0 ? (
          sortedPayMonthIndices.map(payMonthListIndex => {
            const currentPeriod = reportingPeriods[payMonthListIndex];
            const oldestNotReportedPeriod = getOldestNotReportedPeriod(reportingPeriods);
            const isInitiallyExpanded = shouldRowBeExpanded(
              currentPeriod,
              oldestNotReportedPeriod || undefined,
              currentPeriod.payMonthStatus
            );
            return (
              <PayReportsTableRow
                key={`pay-reports-table-row-${currentPeriod.payMonth}`}
                payMonth={currentPeriod}
                isInitiallyExpanded={isInitiallyExpanded}
              />
            );
          })
        ) : (
          <NoPayReports />
        )}
      </Table.Body>
    </Table>
  );
};

const shouldRowBeExpanded = (
  currentPayMonthOverview: PayMonthOverviewViewModel,
  oldestNotReportedPayMonth: PayMonthOverviewViewModel | undefined,
  payMonthStatus: PayMonthStatusEnum
): boolean => {
  if (
    payMonthStatus !== PayMonthStatusEnum.ReportingNotDone &&
    payMonthStatus !== PayMonthStatusEnum.ReportingApproved
  ) {
    return true;
  }
  if (payMonthStatus === PayMonthStatusEnum.ReportingApproved && isReportingPeriodOpen(currentPayMonthOverview)) {
    return true;
  } else if (oldestNotReportedPayMonth) {
    return currentPayMonthOverview.payMonth === oldestNotReportedPayMonth.payMonth;
  } else {
    return false;
  }
};

const getOldestNotReportedPeriod = (
  reportingPeriods: PayMonthOverviewViewModel[]
): PayMonthOverviewViewModel | undefined => {
  const notReportedPeriods = reportingPeriods.filter(
    period => period.payMonthStatus === PayMonthStatusEnum.ReportingNotDone
  );

  if (notReportedPeriods.length > 1) {
    // There are more 'NotReported' periods present, find the oldest one
    return notReportedPeriods.reduce((oldestPeriod, currentPeriod) => {
      const oldestPeriodFromDate = new Date(oldestPeriod.periodFromDate);
      const currentPeriodFromDate = new Date(currentPeriod.periodFromDate);
      return oldestPeriodFromDate <= currentPeriodFromDate ? oldestPeriod : currentPeriod;
    });
  } else {
    return notReportedPeriods[0] || undefined;
  }
};

const NoPayReports = () => {
  const { t } = useTypedTranslation();
  return (
    <Table.Row>
      <Table.Cell colSpan={2}>
        <Flex justifyContent="center">
          <Headings.H6>{t('section.reportDetails.noPayReports')}</Headings.H6>
        </Flex>
      </Table.Cell>
    </Table.Row>
  );
};
